import React, { FunctionComponent, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, SilentRequest } from "@azure/msal-browser";
import { scopes } from "../../../b2cConfiguration";

type AuthState = 'loading' | 'authenticated' | 'failed';

interface IframeAuthBoundaryProps {
    domainHint: string;
    children: React.ReactNode;
}

export const IframeAuthBoundary: FunctionComponent<IframeAuthBoundaryProps> = ({
    domainHint,
    children,
}) => {
    const { instance, accounts } = useMsal();
    const [authState, setAuthState] = useState<AuthState>('loading');

    useEffect(() => {
        const attemptSilentAuth = async () => {
            console.info('[IframeAuth] Starting ssoSilent attempt', {
                domainHint,
                cachedAccounts: accounts.length,
            });

            const silentRequest: SilentRequest = {
                scopes: scopes,
                extraQueryParameters: { domain_hint: domainHint },
            };

            if (accounts.length > 0) {
                silentRequest.account = accounts[0];
                console.info('[IframeAuth] Using cached account for ssoSilent', {
                    accountId: accounts[0].localAccountId,
                });
            }

            try {
                const response = await instance.ssoSilent(silentRequest);
                if (response?.account) {
                    instance.setActiveAccount(response.account);
                }
                console.info('[IframeAuth] ssoSilent succeeded', {
                    accountId: response?.account?.localAccountId,
                });
                setAuthState('authenticated');
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    console.warn(
                        '[IframeAuth] No active B2C session (InteractionRequiredAuthError). ' +
                        'User must log in on the partner site first.'
                    );
                } else {
                    console.error('[IframeAuth] ssoSilent failed with unexpected error', error);
                }
                setAuthState('failed');
            }
        };

        attemptSilentAuth();
    }, [instance, accounts, domainHint]);

    if (authState === 'loading') {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Authenticating...</div>;
    }

    if (authState === 'failed') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Unable to authenticate automatically.</p>
                <p>Please ensure you are logged in on the partner site and try again.</p>
            </div>
        );
    }

    return <>{children}</>;
};
